import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { ThreeDotsVertical } from "react-bootstrap-icons";
import ConfirmDeleteDocumentModal from "@/components/ConfirmDeleteDocumentModal";
import { useDocumentApi } from "@/hooks/useApi";
import { queryClient } from "@/lib/queryClient";
import type { DocumentClient } from "@/lib/types";
import {
  formatShortDate,
  getDocumentStatusText,
  getDocumentSubTypeName,
} from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { toast } from "../ui/toast";
import { DocumentIcon } from "./DocumentList";

type Props = {
  document: DocumentClient;
  updateDocuments: () => void;
};

export function ArchiveDocumentRow(props: Props) {
  const documentApi = useDocumentApi();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const restoreMutation = useMutation({
    mutationFn: () =>
      documentApi.documentUpdateDocument({
        documentId: props.document.id,
        document: {
          ...props.document,
          isArchived: false,
        },
      }),
    onMutate: () => {
      props.updateDocuments();
    },
    onSuccess: () => {
      toast.success(`${props.document.name} was restored`);
    },
    onError: () => {
      toast.error(`Error restoring ${props.document.name}`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["archivedDocuments"] });
    },
  });

  return (
    <>
      <ConfirmDeleteDocumentModal
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        document={props.document}
        onDelete={props.updateDocuments}
      />
      <tr>
        <td>
          <DocumentIcon extension="pdf" />
        </td>
        <td>{props.document.name}</td>
        <td>{props.document.info?.caseNumber}</td>
        <td>
          {props.document.info?.documentSubType &&
            getDocumentSubTypeName(
              props.document.info.documentSubType,
              props.document.info.jurisdiction ?? "",
            )}
        </td>
        <td>
          {props.document.uploadDate
            ? formatShortDate(props.document.uploadDate)
            : "-"}
        </td>
        <td>{getDocumentStatusText(props.document.status).status}</td>
        <td className="text-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ThreeDotsVertical size={14} color="var(--text)" />
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => restoreMutation.mutate()}>
                  Restore
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Permanently Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenu>
        </td>
      </tr>
    </>
  );
}
